import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { translate } from '../../i18n';
import { TimeWindowDto } from '../../models/time-window/dto/time-window.dto';
import ErrorState from '../../components/states/ErrorState';
import { useDeleteTimeWindowGroup } from './hooks/useDeleteTimeWindowGroup';
import { useGetTimeWindows } from './hooks/useGetTimeWindows';
import { TimeWindowFormModal } from './TimeWindowFormModal';

interface TimeWindowListProps {
  groupId: string;
  onGroupDeleted?: () => void;
}

interface GroupedTimeWindow {
  startTime: Date;
  endTime: Date;
  windowIds: string[];
  daysOfWeek: number[];
}

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: 'numeric',
});

const dayFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
});

const getTimeKey = (startTime: Date, endTime: Date) => {
  return `${timeFormatter.format(startTime)}-${timeFormatter.format(endTime)}`;
};

const groupTimeWindows = (windows: TimeWindowDto[]): GroupedTimeWindow[] => {
  const groups = new Map<string, GroupedTimeWindow>();

  windows.forEach(window => {
    const startTime = new Date(window.startTime);
    const endTime = new Date(window.endTime);
    const timeKey = getTimeKey(startTime, endTime);
    const dayOfWeek = startTime.getDay() || 7; // Convert Sunday (0) to 7

    if (!groups.has(timeKey)) {
      groups.set(timeKey, {
        startTime,
        endTime,
        windowIds: [window.id],
        daysOfWeek: [dayOfWeek],
      });
    } else {
      const group = groups.get(timeKey)!;
      group.windowIds.push(window.id);
      if (!group.daysOfWeek.includes(dayOfWeek)) {
        group.daysOfWeek.push(dayOfWeek);
      }
    }
  });

  return Array.from(groups.values());
};

const formatDaysOfWeek = (daysOfWeek: number[]) => {
  const sortedDays = [...daysOfWeek].sort((a, b) => a - b);
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - monday.getDay() + 1);

  return sortedDays.map(day => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + (day === 7 ? 0 : day) - 1);
    return dayFormatter.format(date);
  }).join(', ');
};

export function TimeWindowList({ groupId, onGroupDeleted }: TimeWindowListProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: windows = [], isLoading, isError } = useGetTimeWindows(groupId);
  const deleteGroupMutation = useDeleteTimeWindowGroup();

  const handleDeleteGroup = () => {
    if (!window.confirm(translate('time_windows.confirm_delete_group'))) return;
    deleteGroupMutation.mutate(groupId, {
      onSuccess: () => onGroupDeleted?.(),
    });
  };

  const groupedWindows = useMemo(() => groupTimeWindows(windows), [windows]);

  if (isLoading) {
    return <Box>{translate('time_windows.loading')}</Box>;
  }

  if (isError) {
    return <ErrorState message={translate('time_windows.error_loading')} />;
  }

  return (
    <VStack spacing={4} align="stretch">
      <HStack justify="flex-end" spacing={2}>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="brand"
          onClick={() => setIsCreateModalOpen(true)}
        >
          {translate('time_windows.add_window')}
        </Button>
        <Button
          leftIcon={<DeleteIcon />}
          colorScheme="red"
          variant="ghost"
          onClick={handleDeleteGroup}
          isLoading={deleteGroupMutation.isLoading}
        >
          {translate('time_windows.delete_group')}
        </Button>
      </HStack>

      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>{translate('time_windows.time_range')}</Th>
            <Th>{translate('dayPicker.selectedDays')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {groupedWindows.map((group) => (
            <Tr key={group.windowIds.join('-')}>
              <Td>
                {timeFormatter.format(group.startTime)} - {timeFormatter.format(group.endTime)}
              </Td>
              <Td>
                <Text>{formatDaysOfWeek(group.daysOfWeek)}</Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <TimeWindowFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        groupId={groupId}
      />
    </VStack>
  );
}